import {
  Component,
  InjectionToken,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { AppSettingService } from '@rd/core/services/app-setting/app-setting.service';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FormBuilderService } from '../../services/form-builder.service';
import { FormBuilderInputComponentType, InputBaseType } from '../../types';

export const FORM_BUILDER_INPUT_TOKEN =
  new InjectionToken<FormBuilderInputComponentType>('FormBuilder_Input');

@Component({
  selector: 'rd-form-builder-input',
  templateUrl: './form-builder-input.component.html',
  styleUrls: ['./form-builder-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FormBuilderInputComponent,
      multi: true,
    },
    {
      provide: FORM_BUILDER_INPUT_TOKEN,
      useExisting: FormBuilderInputComponent,
      multi: true,
    },
  ],
})
export class FormBuilderInputComponent
  implements
    FormBuilderInputComponentType,
    ControlValueAccessor,
    OnInit,
    OnDestroy
{
  @Input() inputProps: InputBaseType;

  matFormFieldAppearance: MatFormFieldAppearance;
  props: InputBaseType;
  formGroup: UntypedFormGroup;
  formControl: AbstractControl;

  isSubmitted$ = new Subject<boolean>();
  destroy$ = new Subject<boolean>();

  onChange: (param: any) => void;
  onTouched: () => void;

  constructor(
    public formGroupDirective: FormGroupDirective,
    private appSettingService: AppSettingService,
    private formBuilderService: FormBuilderService,
  ) {}

  writeValue(value: any): void {
    this.setInitialValue(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnInit(): void {
    this.props = this.formBuilderService.buildInputProps(this.inputProps);
    this.matFormFieldAppearance = this.appSettingService.matFormFieldAppearance;

    //
    this.formGroup = this.formGroupDirective.control;
    this.formGroup.registerControl(this.props.key, new UntypedFormControl());
    this.formControl = this.formGroup.controls[this.props.key];

    if (this.props.disabled) {
      this.formControl.disable();
    } else if (this.props.syncValidators) {
      this.formControl.addValidators(
        // @ts-ignore
        this.props.syncValidators,
      );
    }

    // change submit status into
    this.isSubmitted$.pipe(takeUntil(this.destroy$), take(1)).subscribe(() => {
      // check required on Submit
      if (this.props.requiredOnSubmit) {
        this.formControl.addValidators(Validators.required);
        this.formControl.updateValueAndValidity();
        this.formControl.markAsTouched();
      }
    });

    this.formControl.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  isEmpty(): boolean {
    return this.validateAfterFormIsTouched(
      () => !!this.formControl.hasError('required'),
    );
  }

  isInvalidPattern(): boolean {
    return this.validateAfterFormIsTouched(
      () => !!this.formControl.hasError('pattern'),
    );
  }

  get inputLabelStyleName(): string | null {
    return this.props.className ? this.props.className + '-label' : null;
  }

  get inputErrorEmptyStyleName(): string | null {
    return this.props.className ? this.props.className + '-empty' : null;
  }

  get inputErrorInvalidStyleName(): string | null {
    return this.props.className ? this.props.className + '-invalid' : null;
  }

  get inputStyleName(): string | null {
    const inputTypeAffix = this.props.type === 'select' ? '-select' : '-input';

    return this.props.className ? this.props.className + inputTypeAffix : null;
  }

  // Must ensure form is touched already, as specs fail unless this check is present
  private validateAfterFormIsTouched(validateFn: () => boolean): boolean {
    return !!this.formControl.touched && validateFn();
  }

  private setInitialValue(value: any): void {
    if (value) {
      this.formControl.setValue(value);
    } else if (this.props.prefilledValue) {
      this.formControl.setValue(this.props.prefilledValue);
    }
  }
}
